import { BulkCategoryDialog } from '@/components/products/BulkCategoryDialog'
import { BulkDeleteDialog } from '@/components/products/BulkDeleteDialog'
import { BulkEditDialog } from '@/components/products/BulkEditDialog'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Input } from '@/components/ui/input'
import { Pagination } from '@/components/ui/pagination'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { useToast } from '@/hooks/use-toast'
import apiClient from '@/lib/apiClient'
import type { Category } from '@/types/api'
import { ChevronDown, Edit, Eye, EyeOff, Folder, Plus, Star, StarOff, Trash2 } from 'lucide-react'
import React, { useMemo, useState } from 'react'

interface Product {
  id: string
  name: string
  sku: string
  price: number
  priceMin?: number
  priceMax?: number
  stock: number
  variantCount?: number
  hasVariants?: boolean
  status: 'active' | 'inactive' | 'draft'
  featured: boolean
  category: string
  image?: string
  createdAt: string
}

interface ProductListProps {
  className?: string
  products: Product[]
  isLoading?: boolean
  categories: Category[]
  searchTerm?: string
  statusFilter?: string
  categoryFilter?: string
  currentPage?: number
  itemsPerPage?: number
  totalItems?: number
  totalPages?: number
  onSearchChange?: (_e: React.ChangeEvent<HTMLInputElement>) => void
  onStatusFilterChange?: (_e: React.ChangeEvent<HTMLSelectElement>) => void
  onCategoryFilterChange?: (_e: React.ChangeEvent<HTMLSelectElement>) => void
  onPageChange?: (_page: number) => void
  onItemsPerPageChange?: (_itemsPerPage: number) => void
  onAddProduct: () => void
  onViewProduct: (_id: string) => void
  onDeleteProduct: (_id: string) => void
  onToggleFeatured: (_id: string, _featured: boolean) => void
  onToggleStatus: (_id: string, _status: Product['status']) => void
  onRefresh: () => void
}

export const ProductList: React.FC<ProductListProps> = ({
  className,
  products,
  isLoading = false,
  categories,
  searchTerm = '',
  statusFilter = 'all',
  categoryFilter = 'all',
  currentPage = 1,
  itemsPerPage = 10,
  totalItems = 0,
  totalPages = 0,
  onSearchChange,
  onStatusFilterChange,
  onCategoryFilterChange,
  onPageChange,
  onItemsPerPageChange,
  onAddProduct,
  onViewProduct,
  onDeleteProduct,
  onToggleFeatured,
  onToggleStatus,
  onRefresh,
}) => {
  const { toast } = useToast()
  const [selectedProducts, setSelectedProducts] = useState<string[]>([])

  // Dialog states
  const [showBulkEditDialog, setShowBulkEditDialog] = useState(false)
  const [showBulkCategoryDialog, setShowBulkCategoryDialog] = useState(false)
  const [showBulkDeleteDialog, setShowBulkDeleteDialog] = useState(false)
  const [isBulkOperationLoading, setIsBulkOperationLoading] = useState(false)

  // Simple handlers - just call parent handlers
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (onSearchChange) {
      onSearchChange(e)
    }
  }

  const handleStatusFilterChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    if (onStatusFilterChange) {
      onStatusFilterChange(e)
    }
  }

  const handleCategoryFilterChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    if (onCategoryFilterChange) {
      onCategoryFilterChange(e)
    }
  }

  const filteredProducts = useMemo(() => {
    return products.filter((product) => {
      const query = searchTerm.toLowerCase()
      const matchesSearch =
        product.name.toLowerCase().includes(query) ||
        product.sku.toLowerCase().includes(query) ||
        product.category.toLowerCase().includes(query)

      const matchesStatus = statusFilter === 'all' || product.status === statusFilter

      // Find the category ID from the categories prop
      const selectedCategory = categories.find((cat) => cat.id === categoryFilter)
      const matchesCategory =
        categoryFilter === 'all' || (selectedCategory && product.category === selectedCategory.name)

      return matchesSearch && matchesStatus && matchesCategory
    })
  }, [products, searchTerm, statusFilter, categoryFilter, categories])

  const handleSelectAll = () => {
    if (selectedProducts.length === filteredProducts.length) {
      setSelectedProducts([])
    } else {
      setSelectedProducts(filteredProducts.map((p) => p.id))
    }
  }

  const handleSelectProduct = (productId: string) => {
    setSelectedProducts((prev) =>
      prev.includes(productId) ? prev.filter((id) => id !== productId) : [...prev, productId]
    )
  }

  // Bulk operation handlers
  const handleBulkEdit = async (data: {
    action: string
    product_ids: string[]
    data?: Record<string, unknown>
  }) => {
    setIsBulkOperationLoading(true)
    try {
      await apiClient.bulkUpdateProducts({
        product_ids: data.product_ids,
        action: data.action as
          | 'activate'
          | 'deactivate'
          | 'delete'
          | 'update_price'
          | 'update_stock'
          | 'update_category',
        data: data.data,
      })
      toast({
        title: 'Success',
        description: `Bulk ${data.action} completed successfully`,
      })
      setSelectedProducts([])
      onRefresh()
    } catch (error) {
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Bulk operation failed',
        variant: 'destructive',
      })
    } finally {
      setIsBulkOperationLoading(false)
    }
  }

  const handleBulkCategoryChange = async (categoryId: string, productIds: string[]) => {
    setIsBulkOperationLoading(true)
    try {
      await apiClient.bulkUpdateProducts({
        product_ids: productIds,
        action: 'update_category',
        data: { category_id: categoryId },
      })
      toast({
        title: 'Success',
        description: `Category updated for ${productIds.length} product${productIds.length > 1 ? 's' : ''}`,
      })
      setSelectedProducts([])
      onRefresh()
    } catch (error) {
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to update category',
        variant: 'destructive',
      })
    } finally {
      setIsBulkOperationLoading(false)
    }
  }

  const handleBulkDelete = async (productIds: string[]) => {
    setIsBulkOperationLoading(true)
    try {
      await apiClient.bulkUpdateProducts({
        product_ids: productIds,
        action: 'delete',
      })
      toast({
        title: 'Success',
        description: `${productIds.length} product${productIds.length > 1 ? 's' : ''} deleted successfully`,
      })
      setSelectedProducts([])
      onRefresh()
    } catch (error) {
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to delete products',
        variant: 'destructive',
      })
    } finally {
      setIsBulkOperationLoading(false)
    }
  }

  const getStatusVariant = (status: Product['status']) => {
    switch (status) {
      case 'active':
        return 'default'
      case 'inactive':
        return 'secondary'
      case 'draft':
        return 'outline'
      default:
        return 'secondary'
    }
  }

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0,
    }).format(price)
  }

  const formatPriceRange = (product: Product) => {
    const minPrice = product.priceMin ?? product.price
    const maxPrice = product.priceMax ?? product.price

    if (
      product.hasVariants &&
      product.variantCount &&
      product.variantCount > 0 &&
      minPrice !== maxPrice
    ) {
      return `${formatPrice(minPrice)} - ${formatPrice(maxPrice)}`
    }

    return formatPrice(product.price)
  }

  const getStockBadgeVariant = (stock: number) => {
    if (stock === 0) return 'destructive'
    if (stock < 5) return 'outline'
    return 'default'
  }

  const getStockText = (stock: number) => {
    return String(stock)
  }

  const formatDate = (value: string) => {
    const date = new Date(value)
    if (Number.isNaN(date.getTime())) return value

    const day = String(date.getDate()).padStart(2, '0')
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const year = String(date.getFullYear())

    return `${day}-${month}-${year}`
  }

  return (
    <>
      <Card className={className}>
        <CardHeader>
          <CardTitle>Products</CardTitle>
          <div className="flex items-center justify-between">
            <div className="flex items-center flex-1 space-x-2">
              <Input
                placeholder="Search products..."
                value={searchTerm}
                onChange={handleSearchChange}
                className="flex-1"
              />

              <div className="relative">
                <select
                  value={statusFilter}
                  onChange={handleStatusFilterChange}
                  className="flex h-10 rounded-md border border-input bg-background pl-3 pr-8 py-2 text-sm ring-offset-background appearance-none"
                >
                  <option value="all">All Status</option>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                  <option value="draft">Draft</option>
                </select>
                <ChevronDown className="pointer-events-none absolute right-2 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              </div>

              <div className="relative">
                <select
                  value={categoryFilter}
                  onChange={handleCategoryFilterChange}
                  className="flex h-10 rounded-md border border-input bg-background pl-3 pr-8 py-2 text-sm ring-offset-background appearance-none"
                >
                  <option value="all">All Categories</option>
                  {categories
                    .sort((a, b) => {
                      // Sort by parent first, then by name
                      if (a.parent_id && !b.parent_id) return 1
                      if (!a.parent_id && b.parent_id) return -1
                      return a.name.localeCompare(b.name)
                    })
                    .map((category) => (
                      <option key={category.id} value={category.id}>
                        {category.parent_id ? '  └ ' : ''}
                        {category.name}
                      </option>
                    ))}
                </select>
                <ChevronDown className="pointer-events-none absolute right-2 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              </div>
            </div>
            <div className="flex items-center space-x-2 ml-4">
              <Button onClick={onAddProduct}>
                <Plus className="h-4 w-4 mr-2" />
                Add Product
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="outline"
                    size="default"
                    className="h-10 min-h-[40px] flex items-center box-border"
                    style={{ height: '42px' }}
                    disabled={selectedProducts.length === 0}
                  >
                    <Edit className="h-4 w-4 mr-2" />
                    Bulk Actions
                    <ChevronDown className="h-4 w-4 ml-2" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => setShowBulkEditDialog(true)}>
                    <Edit className="h-4 w-4 mr-2" />
                    Bulk Edit
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setShowBulkCategoryDialog(true)}>
                    <Folder className="h-4 w-4 mr-2" />
                    Bulk Category Change
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => setShowBulkDeleteDialog(true)}
                    className="text-red-600 focus:text-red-600"
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Bulk Delete
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>

          {/* Selected count indicator */}
          {selectedProducts.length > 0 && (
            <div className="text-sm text-muted-foreground">{selectedProducts.length} selected</div>
          )}
        </CardHeader>
        <CardContent>
          {isLoading && (
            <div className="mb-4 text-sm text-muted-foreground">Loading products...</div>
          )}

          {/* Table */}
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12">
                    <input
                      type="checkbox"
                      className="rounded border-gray-300"
                      checked={selectedProducts.length === filteredProducts.length}
                      onChange={handleSelectAll}
                    />
                  </TableHead>
                  <TableHead>Product</TableHead>
                  <TableHead>SKU</TableHead>
                  <TableHead>Price</TableHead>
                  <TableHead>Stock</TableHead>
                  <TableHead>Variants</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Featured</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredProducts.map((product) => (
                  <TableRow key={product.id}>
                    <TableCell>
                      <input
                        type="checkbox"
                        className="rounded border-gray-300"
                        checked={selectedProducts.includes(product.id)}
                        onChange={() => handleSelectProduct(product.id)}
                      />
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center space-x-3">
                        {product.image && (
                          <img
                            src={product.image}
                            alt={product.name}
                            className="h-10 w-10 rounded object-cover"
                          />
                        )}
                        <div>
                          <div className="font-medium">{product.name}</div>
                          {product.hasVariants && (product.variantCount ?? 0) > 0 && (
                            <Badge variant="outline" className="mt-1 text-xs">
                              {product.variantCount} variants
                            </Badge>
                          )}
                          {product.featured && (
                            <Badge variant="secondary" className="text-xs">
                              <Star className="h-3 w-3 mr-1" />
                              Featured
                            </Badge>
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="font-mono text-sm">{product.sku}</TableCell>
                    <TableCell>{formatPriceRange(product)}</TableCell>
                    <TableCell>
                      <Badge variant={getStockBadgeVariant(product.stock)}>
                        {getStockText(product.stock)}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {product.hasVariants ? (
                        <Badge variant="outline">{product.variantCount || 0}</Badge>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <span className="text-sm">{product.category}</span>
                    </TableCell>
                    <TableCell>
                      <Badge variant={getStatusVariant(product.status)}>{product.status}</Badge>
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onToggleFeatured(product.id, !product.featured)}
                        className="p-1"
                      >
                        {product.featured ? (
                          <Star className="h-4 w-4 text-yellow-500 fill-current" />
                        ) : (
                          <StarOff className="h-4 w-4 text-muted-foreground" />
                        )}
                      </Button>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm text-muted-foreground">
                        {formatDate(product.createdAt)}
                      </span>
                    </TableCell>
                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm">
                            <ChevronDown className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => onViewProduct(product.id)}>
                            <Eye className="h-4 w-4 mr-2" />
                            View
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() =>
                              onToggleStatus(
                                product.id,
                                product.status === 'active' ? 'inactive' : 'active'
                              )
                            }
                          >
                            <EyeOff className="h-4 w-4 mr-2" />
                            {product.status === 'active' ? 'Deactivate' : 'Activate'}
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => onToggleFeatured(product.id, !product.featured)}
                          >
                            {product.featured ? (
                              <>
                                <StarOff className="h-4 w-4 mr-2" />
                                Remove from Featured
                              </>
                            ) : (
                              <>
                                <Star className="h-4 w-4 mr-2" />
                                Mark as Featured
                              </>
                            )}
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => onDeleteProduct(product.id)}
                            className="text-destructive"
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="mt-4">
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                totalItems={totalItems}
                itemsPerPage={itemsPerPage}
                onPageChange={onPageChange || (() => {})}
                onItemsPerPageChange={onItemsPerPageChange || (() => {})}
              />
            </div>
          )}
        </CardContent>
      </Card>

      {/* Bulk Operation Dialogs */}
      <div>
        <BulkEditDialog
          open={showBulkEditDialog}
          onOpenChange={setShowBulkEditDialog}
          selectedProducts={selectedProducts}
          categories={categories}
          onBulkEdit={handleBulkEdit}
          isLoading={isBulkOperationLoading}
        />

        <BulkCategoryDialog
          open={showBulkCategoryDialog}
          onOpenChange={setShowBulkCategoryDialog}
          selectedProducts={selectedProducts}
          categories={categories}
          onBulkCategoryChange={handleBulkCategoryChange}
          isLoading={isBulkOperationLoading}
        />

        <BulkDeleteDialog
          open={showBulkDeleteDialog}
          onOpenChange={setShowBulkDeleteDialog}
          selectedProducts={selectedProducts}
          onBulkDelete={handleBulkDelete}
          isLoading={isBulkOperationLoading}
        />
      </div>
    </>
  )
}
